import React from 'react';
import generatePDF from 'react-to-pdf';
import {useDispatch, useSelector} from "react-redux";
import {
    setSelectedCountry,
    setSelectedCountryBelongsToOtherRegion,
    setSelectedState,
    resetSelectedState,
    setYear
} from "../features/map/mapSlice";
import {AppDispatch, RootState} from "../store/store";
import {availableYears, isRegion} from "../config";
import {resetPins} from "../features/map/pinsSlice";
import {LATAM_COUNTRY_NAMES, ROMANIA_REGION, USA_COUNTRY_NAMES} from "../features/countries/types";
import {fetchStateStatisticsAsync} from "../features/statistics/stateStatisticsSlice";
import {setStatisticsForSelectedCountry} from "../features/statistics/statisticsSlice";
import {StatisticsData} from "../features/statistics/types";
import {setSelectedDistrict, resetSelectedDistrict} from "../features/map/mapSlice";
import {getDistrictsForState} from "../data/usaDistricts";
import {api} from "../api/api";

const pxToCmRatio = 0.264583333;

const pxToCm = (px: number) => px * pxToCmRatio;

const hide = (toHide: Element[]) => {
    for (const elementsToHideElement of toHide) {
        // @ts-ignore
        elementsToHideElement.style.display = 'none';
    }
}
const show = (toShow: Element[]) => {
    for (const elementsToHideElement of toShow) {
        // @ts-ignore
        elementsToHideElement.style.display = '';
    }
}

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const usePrintToPdf = (ref: React.Ref<HTMLElement>) => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        countriesAll: {data: countriesAll},
        countriesPerUser: {data: countriesPerUser},
    } = useSelector((state: RootState) => state);
    const [inProgress, setInProgress] = React.useState(false);
    const {data: geography} = useSelector((state: RootState) => state.mapApi);


    const createPDF = async (year: string, name: string) => {

        const toHide = Array.from(document.getElementsByClassName('hidden-print'));
        hide(toHide);

        // Center header text for PDF
        const headerGrid = document.querySelector('#Page-id .MuiContainer-root .MuiGrid-container') as HTMLElement | null;
        const headerGridTextAlign = headerGrid?.style.textAlign || '';
        if (headerGrid) {
            headerGrid.style.textAlign = 'center';
        }

        // --- Preserve & adjust styles for non-USA layout ---
        const statsContainer = document.getElementById('stats-container');
        const statsContainerStylesObj = statsContainer ? {
            width: statsContainer.style.width,
            height: statsContainer.style.height,
            margin: statsContainer.style.margin,
        } : null;

        if (statsContainer) {
            statsContainer.style.width = '100%';
            statsContainer.style.height = '31vh';
            statsContainer.style.margin = 'unset';
        }

        const economicSection = document.getElementById('economicSection');
        const economicSectionPaddingTop = economicSection?.style.paddingTop || '';
        const economicSectionPadding = economicSection?.style.padding || '';
        if (economicSection) {
            economicSection.style.padding = '25px';
            economicSection.style.paddingTop = '40px';
        }

        const employmentSection = document.getElementById('employmentSection');
        const employmentSectionPadding = employmentSection?.style.padding || '';
        if (employmentSection) {
            employmentSection.style.padding = '25px';
        }

        const communitySection = document.getElementById('communitySection');
        const communitySectionPadding = communitySection?.style.padding || '';
        if (communitySection) {
            communitySection.style.padding = '25px';
        }

        const impactOverTime = document.getElementById('impactOverTime');
        if (impactOverTime) {
            impactOverTime.style.padding = '25px';
        }

        const statisticsBox = document.getElementById('statistics-box');
        const statisticsBoxPadding = statisticsBox?.style.padding || '';
        if (statisticsBox) {
            statisticsBox.style.padding = '25px';
        }

        const preservedWidth = document.body.style.width;
        document.body.style.width = '1400px';

        const savedRef = {...ref};
        const pageProps = (document.getElementById('Page-id') as HTMLElement).getBoundingClientRect();

        const generate = generatePDF(savedRef as (() => HTMLElement | null), {
            filename: `Infographic_${name}_${year}.pdf`,
            page: {
                format: [pxToCm(1400), pxToCm(pageProps.height + 1)]
            }
        });

        // --- Restore styles ---
        if (economicSection) {
            economicSection.style.padding = economicSectionPadding;
            economicSection.style.paddingTop = economicSectionPaddingTop;
        }
        if (statisticsBox) {
            statisticsBox.style.padding = statisticsBoxPadding;
        }
        if (employmentSection) {
            employmentSection.style.padding = employmentSectionPadding;
        }
        if (communitySection) {
            communitySection.style.padding = communitySectionPadding;
        }
        if (statsContainer && statsContainerStylesObj) {
            statsContainer.style.width = statsContainerStylesObj.width;
            statsContainer.style.height = statsContainerStylesObj.height;
            statsContainer.style.margin = statsContainerStylesObj.margin;
        }
        if (impactOverTime) {
            impactOverTime.style.padding = '140px 25px 50px 25px';
        }

        document.body.style.width = preservedWidth;
        if (headerGrid) {
            headerGrid.style.textAlign = headerGridTextAlign;
        }
        show(toHide);

        await Promise.resolve(generate);
    }

    const generatePDFs = async () => {
        setInProgress(true);
        dispatch(resetPins());

        if (isRegion.usa) {
            await generateUSAPDFs();
        } else {
            await generateLATAMPDFs();
        }

        setInProgress(false);
    }

    // --- PDF generation mode ---
    // 'states'    = national + per-state PDFs (no districts)
    // 'districts' = only district PDFs (iterates states → districts within each)
    // 'all'       = national + states + districts
    const USA_PDF_MODE = 'districts' as 'states' | 'districts' | 'all';

    const generateUSAPDFs = async () => {
        const year = availableYears[availableYears.length - 1]; // '2025'
        dispatch(setYear(year));

        const usaCountry = countriesPerUser.find(c => c.country_name === USA_COUNTRY_NAMES.USA);
        const states = usaCountry?.states ?? [];
        const includeStates = USA_PDF_MODE === 'states' || USA_PDF_MODE === 'all';
        const includeDistricts = USA_PDF_MODE === 'districts' || USA_PDF_MODE === 'all';

        // 1. National-level PDF
        if (includeStates) {
            console.log('Generating national-level PDF...');
            dispatch(resetSelectedState());
            await delay(4000);
            await createPDF(year, 'United_States');
        }

        // 2. Per-state (and optionally per-district) PDFs
        for (let i = 0; i < states.length; i++) {
            const state = states[i];
            console.log(`Processing state: ${state.state_name} (${i + 1}/${states.length})`);
            dispatch(setSelectedState(state));
            dispatch(resetSelectedDistrict());

            const statsResult = await dispatch(fetchStateStatisticsAsync({ stateId: state.state_id, year }));
            if (fetchStateStatisticsAsync.fulfilled.match(statsResult)) {
                const stateStats = (statsResult.payload as StatisticsData[])?.[0];
                if (stateStats) {
                    dispatch(setStatisticsForSelectedCountry(stateStats));
                }
            }

            if (includeStates) {
                await delay(2000);
                await createPDF(year, `${state.state_id}__${state.state_name.replace(/\s+/g, '_')}`);
            }

            if (includeDistricts) {
                const districts = getDistrictsForState(state.state_name);
                for (let j = 0; j < districts.length; j++) {
                    const d = districts[j];
                    const districtNum = d.district ?? 'at-large';
                    console.log(`  District ${districtNum} (${j + 1}/${districts.length})`);
                    dispatch(setSelectedDistrict(districtNum));

                    const districtStats = await api.getDistrictStatistics(state.state_id, districtNum, year);
                    if (districtStats?.length) {
                        dispatch(setStatisticsForSelectedCountry(districtStats[0]));
                    }

                    await delay(2000);
                    await createPDF(year, `${state.state_id}__${state.state_name.replace(/\s+/g, '_')}__district_${districtNum}`);
                }
                dispatch(resetSelectedDistrict());
            }
        }

        // Reset back to national view
        dispatch(resetSelectedState());
    }

    const generateLATAMPDFs = async () => {
        const yearsToGenerate = ['2024'];
        const years = yearsToGenerate.length ? yearsToGenerate : availableYears;
        for (const year of years) {
            console.log('selecting year', year);
            dispatch(setYear(year));
            let counter = 0;
            for (const country of countriesAll) {
                counter++;
                console.log(`selecting country ${country.country_name} (${counter}/${countriesAll.length})`);


                if (country.country_name.toUpperCase() === LATAM_COUNTRY_NAMES.CARICOM.toUpperCase()) {

                    const caricomCountry = countriesAll.find(
                        (country) => country.country_name.toUpperCase() === LATAM_COUNTRY_NAMES.CARICOM.toUpperCase()
                    );

                    const selectedCountryBelongsToCaricom = caricomCountry?.states.find(
                        // @ts-ignore
                        (state) => state.iso3 === geography?.properties?.iso3
                    );

                    dispatch(
                        setSelectedCountryBelongsToOtherRegion(
                            selectedCountryBelongsToCaricom
                        )
                    );
                } else {
                    dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
                }

                dispatch(setSelectedCountry(country));

                await delay(500);

                // @ts-ignore
                const dropDownCountry = document.getElementById('country-select')?.innerText || '';
                if (dropDownCountry || country.country_name === ROMANIA_REGION.ROMANIA ) {
                    await delay(4000);
                    await createPDF(year, country.country_name);
                }
            }
        }
    }

    return {createPDF, generatePDFs, inProgress}
}

export default usePrintToPdf;
